import css_text from '/srv/www/htdocs/loc/app/styles/web_components/textarea.css';

const has_element_internals = !!(window.ElementInternals && 'attachInternals' in HTMLElement.prototype);

function get_shared_sheet() {
	if (get_shared_sheet.cached) {
		return get_shared_sheet.cached;
	}
	const sheet = new CSSStyleSheet();
	sheet.replaceSync(css_text);
	get_shared_sheet.cached = sheet;
	return sheet;
}

export class YalwaTextarea extends HTMLElement {

	static formAssociated = true;

	static get observedAttributes() {
		return ['size', 'disabled', 'readonly', 'error', 'required', 'chars', 'min', 'max', 'chars_label', 'name', 'value', 'placeholder', 'rows', 'form'];
	}

	constructor() {
		super();
		this._internals = has_element_internals ? this.attachInternals() : null;
		this._form_proxy = null;
		if (!this.shadowRoot) {
			this.attachShadow({mode: 'open'});
		}
		this.apply_css();
	}

	async apply_css() {
		await yalwa.basics.check_stylesheet_polyfill_needed();
		this.shadowRoot.adoptedStyleSheets = [get_shared_sheet()];
	}

	connectedCallback() {
		this.initialize_template();
		this._textarea = this.shadowRoot.querySelector('textarea');
		this._container = this.shadowRoot.querySelector('.container');
		this._char_count = this.shadowRoot.querySelector('.js-char_count');

		this._wire_events();
		this._sync_chars();
		this.render();
		this._auto_grow();

		if (this.hasAttribute('value')) {
			this.value = this.getAttribute('value');
		}
		if (this.hasAttribute('placeholder')) {
			this._textarea.placeholder = this.getAttribute('placeholder');
		}
		if (this.hasAttribute('rows')) {
			this._textarea.rows = parseInt(this.getAttribute('rows'), 10);
		}
		if (this.hasAttribute('disabled')) {
			this._textarea.disabled = true;
		}
		if (this.hasAttribute('readonly')) {
			this._textarea.readOnly = true;
		}

		jQuery(this).removeClass('invisible');
	}

	initialize_template() {
		if (!this.shadowRoot.firstChild) {
			const template = document.createElement('template');
			template.innerHTML = `
				<div class="wrapper" part="wrapper">
					<div class="header">
						<label class="label">
							<slot></slot>
						</label>
						<span class="char-count js-char_count" aria-live="polite" style="display:none"></span>
					</div>
					<div class="container container--md" part="container">
						<textarea part="control"></textarea>
					</div>
					<slot name="helper"></slot>
				</div>
			`;
			this.shadowRoot.appendChild(template.content.cloneNode(true));
		}
	}

	attributeChangedCallback(name, old_value, new_value) {
		if (old_value === new_value) {
			return;
		}

		if (name === 'size' && this._container) {
			this.render();
		}

		if (name === 'value' && this._textarea && new_value !== null) {
			const current = this._textarea.value;
			if (new_value !== current) {
				this._textarea.value = new_value;
			}
		}

		if (name === 'disabled' && this._textarea) {
			this._textarea.disabled = new_value !== null;
		}

		if (name === 'readonly' && this._textarea) {
			this._textarea.readOnly = new_value !== null;
		}

		if (name === 'rows' && this._textarea && new_value !== null) {
			this._textarea.rows = parseInt(new_value, 10);
			this._auto_grow();
		}

		if (['name', 'value', 'disabled', 'readonly', 'form', 'error'].includes(name)) {
			this.sync_form_state();
		}

		if (name === 'placeholder' && this._textarea) {
			this._textarea.placeholder = new_value ?? '';
		}

		if (['chars', 'min', 'max', 'chars_label'].includes(name)) {
			this._sync_chars();
		}
	}

	render() {
		if (!this._container) {
			return;
		}

		const size = this.getAttribute('size') || 'md';
		this._container.className = `container container--${size}`;
	}

	/**
	 * Auto-grow: the textarea fills the bordered container (flex stretch via the
	 * container's min-height) and grows with the content — the container grows
	 * along since its height is auto with only a min-height.
	 */
	_auto_grow() {
		if (!this._textarea || !this._container) {
			return;
		}

		this._textarea.style.height = 'auto';

		const container_style = getComputedStyle(this._container);
		const available = this._container.clientHeight
			- parseFloat(container_style.paddingTop)
			- parseFloat(container_style.paddingBottom);
		const needed = this._textarea.scrollHeight;

		this._textarea.style.height = Math.max(available, needed) + 'px';
	}

	_sync_chars() {
		if (!this._char_count || !this.hasAttribute('chars')) {
			if (this._char_count) {
				this._char_count.style.display = 'none';
			}
			if (this._textarea) {
				this._textarea.removeAttribute('maxlength');
			}
			return;
		}

		const current = this._textarea ? this._textarea.value.length : 0;
		const max = parseInt(this.getAttribute('max'), 10);
		const label = this.getAttribute('chars_label') || '';
		const has_max = !isNaN(max);

		if (this._textarea && has_max) {
			this._textarea.maxLength = max;
		} else if (this._textarea) {
			this._textarea.removeAttribute('maxlength');
		}

		let count_text = String(current);
		if (has_max) {
			count_text = current + '/' + max;
		}
		if (label) {
			count_text = label + ': ' + count_text;
		}

		this._char_count.textContent = count_text;
		this._char_count.style.display = 'flex';
	}

	_wire_events() {
		if (!this._textarea) {
			return;
		}

		this._textarea.addEventListener('input', () => {
			this.sync_form_state();
			this._sync_chars();
			this._auto_grow();
			this.dispatchEvent(new Event('input', {bubbles: true}));
		});

		this._textarea.addEventListener('change', () => {
			this.dispatchEvent(new Event('change', {bubbles: true}));
		});
	}

	sync_form_state() {
		const value = this._textarea ? this._textarea.value : '';
		const name = this.getAttribute('name');

		if (has_element_internals && this._internals) {
			this._internals.setFormValue(name ? value : null);
			if (!this.readOnly && this.hasAttribute('error')) {
				const helper_slot = this.shadowRoot.querySelector('slot[name="helper"]');
				const helper_nodes = helper_slot ? helper_slot.assignedNodes() : [];
				const message = helper_nodes.map(n => n.textContent).join('').trim();
				this._internals.setValidity({customError: true}, message || 'Invalid input', this._textarea);
			} else {
				this._internals.setValidity({});
			}
		} else {
			this.ensure_form_proxy();
			if (!this._form_proxy) {
				return;
			}

			const id = this.getAttribute('form');
			const target_form = id ? document.getElementById(id) : this.closest('form');

			if (target_form && this._form_proxy.parentNode !== target_form) {
				target_form.appendChild(this._form_proxy);
			}

			if (name) {
				this._form_proxy.name = name;
				this._form_proxy.value = value;
				this._form_proxy.disabled = this.hasAttribute('disabled');
			} else {
				this._form_proxy.removeAttribute('name');
			}
		}
	}

	ensure_form_proxy() {
		if (this._form_proxy) {
			return;
		}
		const input = document.createElement('input');
		input.type = 'hidden';
		input.style.display = 'none';
		input.setAttribute('aria-hidden', 'true');

		const id = this.getAttribute('form');
		const target_form = id ? document.getElementById(id) : this.closest('form');
		(target_form ?? this).appendChild(input);

		this._form_proxy = input;
	}

	disconnectedCallback() {
		if (this._form_proxy && this._form_proxy.parentNode) {
			this._form_proxy.parentNode.removeChild(this._form_proxy);
		}
	}

	get value() {
		return this._textarea ? this._textarea.value : '';
	}

	set value(val) {
		const str = String(val ?? '');
		if (this._textarea) {
			this._textarea.value = str;
		}
		this.setAttribute('value', str);
		this.sync_form_state();
		this._sync_chars();
		this._auto_grow();
	}

	get disabled() {
		return this.hasAttribute('disabled');
	}

	set disabled(val) {
		if (val) {
			this.setAttribute('disabled', '');
		} else {
			this.removeAttribute('disabled');
		}
	}

	get readOnly() {
		return this.hasAttribute('readonly');
	}

	set readOnly(val) {
		if (val) {
			this.setAttribute('readonly', '');
		} else {
			this.removeAttribute('readonly');
		}
	}

	get name() {
		return this.getAttribute('name');
	}

	set name(val) {
		if (val) {
			this.setAttribute('name', val);
		} else {
			this.removeAttribute('name');
		}
	}

	focus() {
		if (this._textarea) {
			this._textarea.focus();
		}
	}

	blur() {
		if (this._textarea) {
			this._textarea.blur();
		}
	}

	setCustomValidity(message) {
		if (message) {
			this.setAttribute('error', '');
			this._update_helper_slot(message);
		} else {
			this.removeAttribute('error');
			this._update_helper_slot('');
		}
		this.sync_form_state();
	}

	_update_helper_slot(message) {
		const helper_slot = this.shadowRoot.querySelector('slot[name="helper"]');
		if (!helper_slot) {
			return;
		}
		const slotted = helper_slot.assignedElements();
		if (slotted.length < 1) {
			return;
		}
		const inline = slotted[0].querySelector('yalwa-inline-message');
		if (inline) {
			inline.textContent = message;
		}
	}
}

customElements.define('yalwa-textarea', YalwaTextarea);
